import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    InputAdornment,
    Checkbox,
    Tooltip,
    Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    AdminPanelSettings as RoleIcon,
    Save as SaveIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { request } from '../../../../../utils/request';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { AVAILABLE_PERMISSIONS } from '../../permissions';

// Types
interface Role {
    _id: string;
    name: string;
    permissions?: string[];
    description?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface RoleFormData {
    name: string;
    permissions: string[];
    description: string;
}

const RoleListPage: React.FC = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
    } = useForm<RoleFormData>({
        defaultValues: {
            name: '',
            permissions: [],
            description: '',
        },
    });

    const selectedPermissions = watch('permissions');

    // Fetch all roles
    const fetchRoles = useCallback(async () => {
        try {
            setLoading(true);
            const response = await request({
                url: 'role/get-all-roles',
                method: 'get',
            });

            const rolesData = response.data || response.roles || response;
            setRoles(Array.isArray(rolesData) ? rolesData : []);
        } catch (error: any) {
            console.error('Error fetching roles:', error);
            toast.error(error?.message || 'Failed to load roles');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    // Open dialog for add/edit
    const openDialog = (role?: Role) => {
        if (role) {
            setEditingRole(role);
            reset({
                name: role.name,
                permissions: role.permissions || [],
                description: role.description || '',
            });
        } else {
            setEditingRole(null);
            reset({
                name: '',
                permissions: [],
                description: '',
            });
        }
        setDialogOpen(true);
    };

    // Close dialog
    const closeDialog = () => {
        setDialogOpen(false);
        setEditingRole(null);
        reset();
    };

    // Handle create/update role
    const onSubmit = async (data: RoleFormData) => {
        try {
            setSaving(true);

            const payload = {
                name: data.name,
                permissions: data.permissions,
                description: data.description,
            };

            let response;
            if (editingRole) {
                response = await request({
                    url: `role/update-role/${editingRole._id}`,
                    method: 'put',
                    data: payload,
                });
            } else {
                response = await request({
                    url: 'role/create-role',
                    method: 'post',
                    data: payload,
                });
            }

            if (response.success) {
                const successMessage = editingRole ? (response.message || 'Role updated successfully') : (response.message || 'Role created successfully');
                toast.success(successMessage);
                closeDialog();
                await fetchRoles(); // Refresh data immediately
            } else {
                const errorMessage = response.message || 'Operation failed';
                toast.error(errorMessage);
            }
        } catch (error: any) {
            console.error('Error saving role:', error);
            const errorMessage = error || 'Failed to save role';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Handle delete role
    const handleDeleteClick = (role: Role) => {
        setRoleToDelete(role);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!roleToDelete) return;

        try {
            setDeleting(true);
            const response = await request({
                url: `role/delete-role/${roleToDelete._id}`,
                method: 'delete',
            });

            if (response.success) {
                const successMessage = (response.message || 'Role deleted successfully');
                toast.success(successMessage);

                setDeleteConfirmOpen(false);
                setRoleToDelete(null);
                await fetchRoles(); // Refresh data immediately
            } else {
                const errorMessage = response.message || 'Failed to delete role';
                toast.error(errorMessage);

            }
        } catch (error: any) {
            console.error('Error deleting role:', error);
            const errorMessage = error || 'Failed to delete role';
            toast.error(errorMessage);

        } finally {
            setDeleting(false);
        }
    };

    // Filter roles based on search term
    const filteredRoles = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Pagination
    const paginatedRoles = filteredRoles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Handle permission selection
    const handlePermissionChange = (permission: string) => {
        const currentPermissions = selectedPermissions || [];
        if (currentPermissions.includes(permission)) {
            setValue('permissions', currentPermissions.filter(p => p !== permission));
        } else {
            setValue('permissions', [...currentPermissions, permission]);
        }
    };

    // Handle select all permissions in a category
    const handleSelectCategory = (categoryPermissions: string[]) => {
        const currentPermissions = selectedPermissions || [];
        const allSelected = categoryPermissions.every(p => currentPermissions.includes(p));

        if (allSelected) {
            setValue('permissions', currentPermissions.filter(p => !categoryPermissions.includes(p)));
        } else {
            const newPermissions = [...currentPermissions];
            categoryPermissions.forEach(p => {
                if (!newPermissions.includes(p)) {
                    newPermissions.push(p);
                }
            });
            setValue('permissions', newPermissions);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                    Loading roles...
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Brandcrumb />
            </Box>

            <Box sx={{  }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                            Role Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage user roles and permissions
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => openDialog()}
                        sx={{ textTransform: 'none' }}
                    >
                        Create New Role
                    </Button>
                </Box>

                {/* Search Bar */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search roles by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Roles Table */}
                <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'background.default' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Role Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width:'10%' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: "10%" }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRoles.length === 0 ? (
                                    <TableRow >
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No roles found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedRoles.map((role) => (
                                        <TableRow key={role._id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <RoleIcon fontSize="small" color="primary" />
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {role.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {role.description || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {(role.permissions || []).slice(0, 3).map((perm, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={perm}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem', height: 24 }}
                                                        />
                                                    ))}
                                                    {(role.permissions || []).length > 3 && (
                                                        <Chip
                                                            label={`+${role.permissions!.length - 3} more`}
                                                            size="small"
                                                            sx={{ fontSize: '0.7rem', height: 24, bgcolor: 'grey.100' }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {role.createdAt ? new Date(role.createdAt).toLocaleDateString() : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip placement='top' arrow title="Edit Role">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openDialog(role)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip placement='top' arrow title="Delete Role">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(role)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredRoles.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            </Box>

            {/* Add/Edit Role Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 1 }
                }}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight="bold">
                                {editingRole ? 'Edit Role' : 'Create New Role'}
                            </Typography>
                            <IconButton onClick={closeDialog} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            {/* Role Name */}
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: 'Role name is required' }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Role Name"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                    />
                                )}
                            />

                            {/* Description */}
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Description"
                                        multiline
                                        rows={2}
                                        size="small"
                                        placeholder="Brief description of this role"
                                    />
                                )}
                            />

                            {/* Permissions */}
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                                    Permissions
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                                    {AVAILABLE_PERMISSIONS.map((category) => {
                                        const allSelected = category.permissions.every(p => selectedPermissions?.includes(p));
                                        const someSelected = category.permissions.some(p => selectedPermissions?.includes(p));

                                        return (
                                            <Box key={category.category} sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={allSelected}
                                                        indeterminate={someSelected && !allSelected}
                                                        onChange={() => handleSelectCategory(category.permissions)}
                                                    />
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        {category.category}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ ml: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {category.permissions.map((permission) => (
                                                        <Chip
                                                            key={permission}
                                                            label={permission.split(':')[1] || permission}
                                                            size="small"
                                                            onClick={() => handlePermissionChange(permission)}
                                                            color={selectedPermissions?.includes(permission) ? 'primary' : 'default'}
                                                            variant={selectedPermissions?.includes(permission) ? 'filled' : 'outlined'}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    ))}
                                                </Box>
                                                <Divider sx={{ mt: 1.5 }} />
                                            </Box>
                                        );
                                    })}
                                </Paper>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Click on permissions to select/deselect
                                </Typography>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={closeDialog}
                            disabled={saving}
                            size="small"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            size="small"
                        >
                            {saving ? 'Saving...' : (editingRole ? 'Update Role' : 'Create Role')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 1 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                        Delete Role
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete the role <strong>"{roleToDelete?.name}"</strong>?
                        This action cannot be undone.
                    </Typography>
                    {roleToDelete?.permissions && roleToDelete.permissions.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This role has {roleToDelete.permissions.length} permission(s) assigned.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDeleteConfirmOpen(false)}
                        disabled={deleting}
                        size="small"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDelete}
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                        size="small"
                    >
                        {deleting ? 'Deleting...' : 'Delete Role'}
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

export default RoleListPage;